import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2Icon, SparklesIcon, WineIcon, UtensilsIcon, TrendingUpIcon, BookOpenIcon, MegaphoneIcon, DollarSignIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
interface TeamSectionProps {
  teamNumber: number;
  teamName: string;
  lead: string;
  role: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}
export function TeamSection({
  teamNumber,
  teamName,
  lead,
  role,
  icon,
  children,
  defaultExpanded = false
}: TeamSectionProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  return <section id={`team-${teamNumber}`} className="relative mb-8" data-id="element-858">
      <div className="border-l-4 border-[#d4a017]/30 bg-[#0f0f23]/40 backdrop-blur-sm" data-id="element-859">
        <button onClick={() => setExpanded(!expanded)} className="w-full text-left p-8 md:p-10 hover:bg-[#0f0f23]/60 transition-colors duration-300" data-id="element-860">
          <div className="flex items-start justify-between gap-6" data-id="element-861">
            <div className="flex items-start gap-6 flex-1" data-id="element-862">
              <div className="text-[#d4a017]/20 flex-shrink-0" data-id="element-863">{icon}</div>
              <div className="flex-1" data-id="element-864">
                <div className="flex items-center gap-4 mb-3" data-id="element-865">
                  <span className="font-serif text-5xl text-[#d4a017]/20" data-id="element-866">
                    {teamNumber}
                  </span>
                  <div data-id="element-867">
                    <h3 className="font-serif text-2xl md:text-3xl text-[#f5f0e8] mb-1" data-id="element-868">
                      {teamName}
                    </h3>
                    <p className="text-[#d4a017]/70 text-sm tracking-wider uppercase font-light" data-id="element-869">
                      Lead: {lead}
                    </p>
                  </div>
                </div>
                <p className="text-[#f5f0e8]/50 font-light leading-relaxed max-w-3xl" data-id="element-870">
                  {role}
                </p>
              </div>
            </div>
            <div className="flex-shrink-0 mt-2" data-id="element-871">
              {expanded ? <ChevronUpIcon className="w-6 h-6 text-[#d4a017]/40" data-id="element-872" /> : <ChevronDownIcon className="w-6 h-6 text-[#d4a017]/40" data-id="element-873" />}
            </div>
          </div>
        </button>

        <AnimatePresence data-id="element-874">
          {expanded && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3
        }} className="overflow-hidden" data-id="element-875">
              <div className="border-t border-[#d4a017]/10" data-id="element-876">{children}</div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
}
/* ─── TEAM CONTENT BLOCKS ─── */
const contentBlock = (title: string, description: string, items: string[]) => <div className="border border-[#d4a017]/10 bg-[#0a0a0f]/60 p-8" data-id="element-877">
    <h4 className="font-serif text-xl text-[#f5f0e8] mb-4" data-id="element-878">{title}</h4>
    <p className="text-[#f5f0e8]/50 font-light leading-relaxed mb-6" data-id="element-879">
      {description}
    </p>
    <ul className="space-y-3" data-id="element-880">
      {items.map((item, i) => <li key={i} className="flex items-start gap-3 text-[#f5f0e8]/40 text-sm font-light" data-id="element-881">
          <span className="w-1.5 h-1.5 rounded-full bg-[#d4a017]/40 mt-2 flex-shrink-0" data-id="element-882"></span>
          {item}
        </li>)}
    </ul>
  </div>;
export function AThinkStudioContent() {
  return <div className="p-8 md:p-10 space-y-8" data-id="element-883">
      {contentBlock('Generative AI Visual Production', 'AThink Studio is responsible for creating all AI-generated visual content for the five portals — from historical recreations to immersive environments that transport guests to 1825 Lahore.', ['360° AI-generated environments for each portal activation', 'Historical character visualizations and period-accurate settings', 'Dynamic visual transitions synchronized with narrative flow', 'Real-time rendering and projection mapping coordination'])}

      {contentBlock('Acting Videos with Seadance', 'In partnership with Seadance, AThink Studio produces bilingual acting videos featuring the five historical figures — delivered in both Punjabi and English to ensure accessibility and cultural authenticity.', ['Scripted performances by professional actors in period costume', 'Bilingual delivery (Punjabi and English) for each portal character', 'Green screen filming with AI background integration', 'Voice direction and cultural consultation with History Team'])}

      {/* AI Visual Samples Video */}
      <div className="border border-[#d4a017]/20 bg-[#0f0f23]/80 backdrop-blur-sm" data-id="element-884">
        <div className="aspect-video bg-[#0a0a0f] relative overflow-hidden" data-id="element-885">
          <iframe className="w-full h-full" src="https://www.youtube.com/embed/BW42P0mU8BU" title="AI Visual Samples" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-886"></iframe>
        </div>
        <div className="p-6 border-t border-[#d4a017]/10" data-id="element-887">
          <h3 className="font-serif text-lg text-[#f5f0e8] mb-1" data-id="element-888">
            AI Visual Samples
          </h3>
          <p className="text-[#f5f0e8]/40 text-sm font-light" data-id="element-889">
            AI-generated visual samples showcasing the immersive worlds and
            design direction for the experience.
          </p>
        </div>
      </div>
    </div>;
}
export function HistoryTeamContent() {
  return <div className="p-8 md:p-10 space-y-8" data-id="element-890">
      {contentBlock('Historical Research & Accuracy', 'Led by Ajaypal, the History Team ensures every element of the experience — from character portrayals to visual details — is historically accurate and culturally respectful.', ['Primary source research on the five portal characters and Maharaja Ranjit Singh', 'Period-accurate costume, architecture, and artifact consultation', 'Cultural and religious sensitivity review across all content', 'Fact-checking of all narrative scripts and visual representations'])}

      {contentBlock('Cross-Team Advisory', 'The History Team works closely with every production team to maintain historical integrity while supporting creative storytelling.', ['Review of AI-generated visuals for historical accuracy (with AThink Studio)', 'Consultation on food authenticity and period recipes (with Food Team)', 'Verification of story arc and character motivations (with Secret Location)', 'Educational content development for school programs (with Sales Team)'])}

      {contentBlock('Community & Academic Partnerships', 'Building relationships with Sikh historians, community leaders, and academic institutions to ensure the experience honors the legacy of the Sikh Kingdoms.', ['Collaboration with ROM Sikh Art and Culture Gallery advisors', 'Consultation with Gurdwara historical committees', 'Academic peer review from Sikh Studies scholars', 'Community feedback sessions with elders and cultural leaders'])}
    </div>;
}
export function PRMarketingTeamContent() {
  return <div className="p-8 md:p-10 space-y-8" data-id="element-891">
      {contentBlock('Media Strategy & Outreach', 'The PR & Marketing Team drives awareness and attendance through strategic media partnerships, influencer collaborations, and community engagement.', ['Press releases and media kit distribution to cultural, lifestyle, and tourism outlets', 'Influencer and creator partnerships for authentic storytelling', 'Feature placements in Toronto Life, blogTO, and cultural publications', 'Radio and podcast interviews highlighting the immersive experience'])}

      {contentBlock('Community Marketing', 'Grassroots marketing through Gurdwaras, cultural organizations, and community networks to build trust and excitement.', ['Partnership announcements with Gurdwaras and Sikh community organizations', 'Community preview nights and word-of-mouth campaigns', 'Social media content featuring community testimonials and behind-the-scenes', 'Cultural event sponsorships and booth presence at Vaisakhi celebrations'])}

      {contentBlock('Digital & Social Campaigns', 'Multi-platform digital marketing to reach diverse audiences — from heritage-focused families to experience-seeking millennials.', ['Instagram and TikTok content showcasing immersive moments and food', 'Facebook campaigns targeting South Asian diaspora and cultural tourists', 'YouTube video series: behind-the-scenes, creator interviews, historical deep dives', 'Email marketing to ROM members, previous Art of the Sikh Kingdoms attendees'])}
    </div>;
}
export function FinanceTeamContent() {
  return <div className="p-8 md:p-10 space-y-8" data-id="element-892">
      {contentBlock('Budget & Financial Planning', 'The Finance Team ensures the experience is commercially viable while maintaining the quality and cultural integrity required for success.', ['Production budget allocation across all teams and workstreams', 'Venue rental, equipment, and staffing cost management', 'Contingency planning for technical and operational risks', 'Monthly financial reporting and variance analysis'])}

      {contentBlock('Revenue Projections & Pricing', 'Developing ticket pricing strategy and revenue models that balance accessibility with sustainability.', ['Tiered pricing: General Admission, Premium Experience, Private Events', 'Group sales strategy for schools, Gurdwaras, and corporate bookings', 'Sponsorship revenue targets and partnership tier pricing', 'Merchandise and digital souvenir revenue streams'])}

      {contentBlock('Commercial Viability Analysis', 'Ensuring the experience can scale and sustain beyond the initial run — with pathways to touring and licensing.', ['Break-even analysis and profitability timeline', 'Scenario planning: conservative, moderate, optimistic attendance', 'Tour feasibility study for other Canadian cities and international markets', 'Licensing model development for wedding and private event adaptations'])}
    </div>;
}
/* ─── SECTION DIVIDERS ─── */
export function GeneralInfoDivider() {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.8
  }} className="py-16 text-center" data-id="element-893">
      <div className="w-20 h-px bg-gradient-to-r from-transparent via-[#d4a017]/40 to-transparent mx-auto mb-6" data-id="element-894"></div>
      <h2 className="font-serif text-3xl md:text-4xl text-[#f5f0e8] mb-3" data-id="element-895">
        General <span className="text-[#d4a017]" data-id="element-896">Information</span>
      </h2>
      <p className="text-[#f5f0e8]/40 text-sm tracking-[0.2em] uppercase font-light" data-id="element-897">
        Understanding the Experience
      </p>
    </motion.div>;
}
export function ProductionTeamsDivider() {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.8
  }} className="py-20 text-center" data-id="element-898">
      <div className="w-20 h-px bg-gradient-to-r from-transparent via-[#d4a017]/40 to-transparent mx-auto mb-6" data-id="element-899"></div>
      <h2 className="font-serif text-4xl md:text-5xl text-[#f5f0e8] mb-4" data-id="element-900">
        Production <span className="text-[#d4a017]" data-id="element-901">Teams</span>
      </h2>
      <p className="text-[#f5f0e8]/40 font-light max-w-2xl mx-auto leading-relaxed mb-3" data-id="element-902">
        Eight specialized teams working together to bring Life in the Sikh
        Kingdoms to life.
      </p>
      <p className="text-[#d4a017]/50 text-xs tracking-[0.2em] uppercase font-light" data-id="element-903">
        Click each team to expand
      </p>
    </motion.div>;
}
export const teamIcons = {
  secretLocation: <Building2Icon className="w-12 h-12" data-id="element-904" />,
  athinkStudio: <SparklesIcon className="w-12 h-12" data-id="element-905" />,
  scentDrink: <WineIcon className="w-12 h-12" data-id="element-906" />,
  food: <UtensilsIcon className="w-12 h-12" data-id="element-907" />,
  sales: <TrendingUpIcon className="w-12 h-12" data-id="element-908" />,
  history: <BookOpenIcon className="w-12 h-12" data-id="element-909" />,
  prMarketing: <MegaphoneIcon className="w-12 h-12" data-id="element-910" />,
  finance: <DollarSignIcon className="w-12 h-12" data-id="element-911" />
};